import React, { useEffect, useRef } from 'react'
import useSectionNav from '../../hooks/useSectionNav'

const FEATURED_COUNT = 3
const FEATURED_CATEGORIES = ['Egg', 'Broiler', 'hatchEgg']

// One row per headline category where possible, topped up with the next latest rates.
const pickFeatured = (rates) => {
    const picked = FEATURED_CATEGORIES
        .map((cat) => rates.find((r) => r.category === cat))
        .filter(Boolean)
    const rest = rates.filter((r) => !picked.includes(r))
    return [...picked, ...rest].slice(0, FEATURED_COUNT)
}

const TRUST_ITEMS = ['Free to Use', 'Pan-India Coverage', 'Vet-Verified Support', 'Daily Rate Updates']

// Rows flash one after another like a live feed, only while the card is visible.
const useRateFlash = (cardRef, rowCount) => {
    useEffect(() => {
        const card = cardRef.current
        if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
        const rows = Array.from(card.querySelectorAll('.rc-row[data-up]'))
        if (!rows.length) return undefined
        let i = 0
        let interval = 0
        const timeouts = new Set()

        const flash = () => {
            if (document.hidden) return
            const row = rows[i % rows.length]
            const cls = row.dataset.up === 'true' ? 'flash-up' : 'flash-down'
            row.classList.add(cls)
            const t = setTimeout(() => { row.classList.remove(cls); timeouts.delete(t) }, 900)
            timeouts.add(t)
            i++
        }
        const observer = new IntersectionObserver(([entry]) => {
            clearInterval(interval)
            if (entry.isIntersecting) interval = setInterval(flash, 2200)
        })
        observer.observe(card)
        return () => {
            observer.disconnect()
            clearInterval(interval)
            timeouts.forEach(clearTimeout)
        }
    }, [cardRef, rowCount])
}

// "Live" only when the newest rate is from today; otherwise say when it was.
const FreshnessBadge = ({ rates }) => {
    const dates = (rates.data || []).map((r) => r.updated).filter(Boolean)
    if (!dates.length) return null
    const newest = new Date(Math.max(...dates))
    if (newest.toDateString() === new Date().toDateString()) {
        return <span className="rc-live"><span className="dot" />Live</span>
    }
    const label = newest.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    return <span className="rc-live">Updated {label}</span>
}

const RateRows = ({ rates }) => {
    if (rates.error || (rates.data && !rates.data.length)) {
        return (
            <div className="rc-row">
                <div>
                    <div className="rc-loc">Rates unavailable right now</div>
                    <div className="rc-sub">Open the app for today's rates</div>
                </div>
            </div>
        )
    }
    if (!rates.data) {
        return Array.from({ length: FEATURED_COUNT }, (_, i) => (
            <div className="rc-row" key={i}>
                <div>
                    <div className="rc-loc">Loading…</div>
                    <div className="rc-sub">&nbsp;</div>
                </div>
                <div className="rc-right"><span className="rc-price">₹—</span></div>
            </div>
        ))
    }
    return pickFeatured(rates.data).map((r) => (
        <div className="rc-row" data-up={r.up} key={`${r.city}-${r.category}`}>
            <div>
                <div className="rc-loc">{r.city}, {r.state}</div>
                <div className="rc-sub">{r.type} Rate</div>
            </div>
            <div className="rc-right">
                <span className="rc-price">{r.price}</span>
                <span className={`tag ${r.up ? 'tag-up' : 'tag-down'}`}>{r.flat ? '–' : r.up ? '▲' : '▼'} {r.change}</span>
            </div>
        </div>
    ))
}

const Hero = ({ rates }) => {
    const cardRef = useRef(null)
    const goToSection = useSectionNav()
    useRateFlash(cardRef, rates.data ? rates.data.length : 0)

    const handleSection = (e, id) => {
        e.preventDefault()
        goToSection(id)
    }

    return (
        <section className="hero" id="home" data-anim>
            <div className="blob blob-1" /><div className="blob blob-2" /><div className="blob blob-3" />
            <div className="wrap hero-grid">
                <div className="hero-copy">
                    <span className="eyebrow">India's Poultry Marketplace</span>
                    <h1>Empowering India's Poultry Industry with Poultryfy</h1>
                    <p className="sub">Track live rates, manage your farms and flocks, book expert vets, and buy or sell poultry products — all from one app built for Indian poultry farmers and traders.</p>
                    <div className="trust-strip">
                        {TRUST_ITEMS.map((item) => (
                            <div className="trust-item" key={item}><span className="dot" />{item}</div>
                        ))}
                    </div>
                    <div className="hero-cta">
                        <a href="#/" className="btn btn-primary" onClick={(e) => handleSection(e, 'download')}>Download the App</a>
                        <a href="#/" className="btn btn-ghost" onClick={(e) => handleSection(e, 'features')}>Explore Features</a>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="rate-float">
                        <div className="rate-card tilt" ref={cardRef}>
                            <div className="rc-title">Today's Price Update<FreshnessBadge rates={rates} /></div>
                            <RateRows rates={rates} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Hero
